import type { ChartRegistryEntry, TableColumnDef } from "./types";
// Re-exported below so every existing `from "./registry"` importer keeps
// working; the table itself lives in conversions.ts because it needs no
// Highcharts (see that file's header).
import { getConvertibleTargets } from "./conversions";
import {
  getTopLevelRows,
  getLineRows,
  getMapRows,
  getGenericTableData,
  getTieredBarRows,
  getQuadrantScatterRows,
  getDataTableChartRows,
} from "./data/transformers";
import { renderBar } from "./renderers/BarRenderer";
import { renderLine } from "./renderers/LineRenderer";
import { renderPie } from "./renderers/PieRenderer";
import { renderSingleBar } from "./renderers/SingleBarRenderer";
import { renderPieDrilldown } from "./renderers/PieDrilldownRenderer";
import { renderFunnel } from "./renderers/FunnelRenderer";
import { renderForceBar } from "./renderers/ForceBarRenderer";
import { renderTieredBar } from "./renderers/TieredBarRenderer";
import { renderQuadrantScatter } from "./renderers/QuadrantScatterRenderer";
import { renderLineWithBar } from "./renderers/LineWithBarRenderer";
import { renderMap } from "./renderers/MapRenderer";
import { renderGroupedBar } from "./renderers/GroupedBarRenderer";
import { renderSpecialMultiLine } from "./renderers/SpecialMultiLineRenderer";
import { renderSentimentScatter } from "./renderers/SentimentScatterRenderer";
import { renderSentimentHistogram } from "./renderers/SentimentHistogramRenderer";

// ---------------------------------------------------------------------------
// Export item lists — Highcharts built-in menu item keys.
// These appear in the hamburger context button on every chart.
// FORCE_BAR omits CSV/XLS because its data structure doesn't map cleanly to rows.
// ---------------------------------------------------------------------------
const STANDARD_EXPORT = [
  "viewFullscreen",
  "printChart",
  "downloadPNG",
  "downloadJPEG",
  "downloadCSV", // Highcharts built-in — downloads chart series as CSV
  "downloadXLS", // Highcharts built-in — downloads chart series as XLS
];

const STANDARD_EXPORT_PDF = [...STANDARD_EXPORT, "downloadPDF"];

// "bar" only — Highcharts' own downloadCSV/downloadXLS are redundant now
// that the backend's has_download_options-driven "Download CSV Data"/
// "Download Case Data" items (ChartFactory.tsx) cover that, and were
// confusing sitting alongside them in the same menu.
const BAR_EXPORT = STANDARD_EXPORT.filter(
  (item) => item !== "downloadCSV" && item !== "downloadXLS",
);

const FORCE_BAR_EXPORT = [
  "viewFullscreen",
  "printChart",
  "downloadPNG",
  "downloadJPEG",
  "downloadPDF",
];

// "funnel" only — Highcharts' own downloadCSV is redundant now that the
// backend's has_download_options-driven "Download Excel Data"/"Download
// Case Data" items (ChartFactory.tsx, wired via FunnelWidget.tsx) cover
// that. downloadXLS is left in on purpose — not asked for, unlike
// BAR_EXPORT above which drops both.
const FUNNEL_EXPORT = STANDARD_EXPORT_PDF.filter(
  (item) => item !== "downloadCSV",
);

// ---------------------------------------------------------------------------
// Table column definitions — used by ChartTableView when rendering the table
// and by exportTableCsv/exportTableXls for the download output.
// ---------------------------------------------------------------------------

const DEFAULT_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "category", header: "Category" },
  { key: "count", header: "Count", align: "end" },
  { key: "percentage", header: "Percentage", align: "end" },
];

const LINE_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "series", header: "Series" },
  { key: "category", header: "Category" },
  { key: "value", header: "Value", align: "end" },
];

const MAP_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "state", header: "State" },
  { key: "cases", header: "Cases", align: "end" },
];

const TIERED_BAR_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "prescriber", header: "Prescriber", align: "center" },
  { key: "pts", header: "Pts", align: "center" },
  { key: "pa_percentage", header: "PA %", align: "center" },
  { key: "abandon_percentage", header: "Abandon %", align: "center" },
  { key: "tier", header: "Tier", align: "center" },
];

const QUADRANT_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "practice", header: "Practice", align: "center" },
  { key: "pts", header: "Total Cases", align: "center" },
  { key: "pa_cases", header: "PA Cases", align: "center" },
  { key: "pa_approved_cases", header: "PA Approved", align: "center" },
  { key: "pa_percentage", header: "PA %", align: "center" },
  { key: "abandon_percentage", header: "Abandon %", align: "center" },
  // cellType "tier-badge" renders a styled pill instead of plain text in ChartTableView.
  { key: "tier", header: "Tier", align: "center", cellType: "tier-badge" },
];

// Shared row extractor for bar/pie/pie_drilldown/funnel — all use the same
// top-level {category, count, percentage} shape.
const defaultGetTableRows = (data: unknown) =>
  getTopLevelRows(data).map((r) => ({
    category: r.category,
    count: r.count,
    percentage: r.percentage,
  }));

// ---------------------------------------------------------------------------
// REGISTRY — the single source of truth for every chart type.
//
// Each entry describes:
//   renderer      — pure function: (data, spec, title) → Highcharts.Options
//   exportItems   — which Highcharts menu items appear in the context button
//   convertibleTo — chart types the user can switch to from this chart
//   fillHeight    — whether the chart should expand to fill its grid cell
//   constructorType — set to "mapChart" for choropleth maps (different Highcharts constructor)
//   tableColumns  — column definitions shown when the user switches to table view
//   getTableRows  — extracts row data from the API response for the table view
//
// To add a new chart type: create a renderer in src/charts/renderers/, then
// add one entry here. Nothing else needs to change.
// ---------------------------------------------------------------------------
const REGISTRY = new Map<string, ChartRegistryEntry>([
  [
    "bar",
    {
      renderer: renderBar,
      exportItems: BAR_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "stacked_bar",
    {
      renderer: renderBar, // same renderer — spec differentiates stacked vs grouped
      exportItems: STANDARD_EXPORT,
      fillHeight: true,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "time_series_bar",
    {
      renderer: renderBar, // same renderer — periods become xAxis categories
      exportItems: STANDARD_EXPORT,
      fillHeight: true,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "pie",
    {
      renderer: renderPie,
      exportItems: STANDARD_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "single_bar",
    {
      // Same {name, value, percentage} flat-array shape as pie/bar, so the
      // same table extraction works unchanged.
      renderer: renderSingleBar,
      exportItems: STANDARD_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "pie_drilldown",
    {
      renderer: renderPieDrilldown,
      exportItems: STANDARD_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows, // reads series[0].data from the drilldown payload
    },
  ],
  [
    "multi_line",
    {
      renderer: renderLine,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: LINE_TABLE_COLUMNS,
      getTableRows: (data) =>
        getLineRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "line",
    {
      renderer: renderLine, // same renderer as multi_line
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: LINE_TABLE_COLUMNS,
      getTableRows: (data) =>
        getLineRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "special_multi_line",
    {
      renderer: renderSpecialMultiLine,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: LINE_TABLE_COLUMNS,
      getTableRows: (data) =>
        getLineRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "funnel",
    {
      renderer: renderFunnel,
      exportItems: FUNNEL_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "force_bar",
    {
      renderer: renderForceBar,
      exportItems: FORCE_BAR_EXPORT, // no CSV/XLS — complex nested data doesn't map to flat rows
      fillHeight: true,
      // No tableColumns — force_bar has no supported conversions
    },
  ],
  [
    "tiered_bar",
    {
      renderer: renderTieredBar,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: TIERED_BAR_TABLE_COLUMNS,
      getTableRows: (data) =>
        getTieredBarRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "quadrant_scatter",
    {
      renderer: renderQuadrantScatter,
      exportItems: STANDARD_EXPORT,
      tableColumns: QUADRANT_TABLE_COLUMNS,
      getTableRows: (data) =>
        getQuadrantScatterRows(data) as unknown as Array<
          Record<string, unknown>
        >,
    },
  ],
  [
    "line_with_bar",
    {
      renderer: renderLineWithBar,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      // No tableColumns — combo chart data doesn't cleanly reduce to bar/pie
    },
  ],
  [
    "map",
    {
      renderer: renderMap,
      exportItems: STANDARD_EXPORT,
      // mapChart uses a different Highcharts constructor (Highcharts.mapChart).
      constructorType: "mapChart",
      tableColumns: MAP_TABLE_COLUMNS,
      getTableRows: (data) =>
        getMapRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "grouped_bar",
    {
      renderer: renderGroupedBar,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "sentiment_scatter",
    {
      renderer: renderSentimentScatter,
      // CSV/XLS included: Highcharts writes one x/y column pair per sentiment
      // band, which is a faithful dump of an aggregated scatter.
      exportItems: STANDARD_EXPORT,
      fillHeight: true,
      // No tableColumns — the built-in table view expects category/count rows,
      // and an (x, y, count, score) bubble doesn't reduce to that.
    },
  ],
  [
    "sentiment_histogram",
    {
      renderer: renderSentimentHistogram,
      exportItems: STANDARD_EXPORT,
      fillHeight: true,
      // Table view intentionally not wired up (no CHART_CONVERSIONS entry
      // either), so the menu shows exports only — same as grouped_bar.
    },
  ],
  [
    "custom_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "custom_table",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "deviation_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "average_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "cards",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "info_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "data_table",
    {
      // data_table is a raw API table — no Highcharts chart is rendered.
      // renderer returns null; DashboardWidgetContent routes this to DataTableWidget.
      renderer: () => null,
      exportItems: STANDARD_EXPORT,
      getTableRows: (data) => getGenericTableData(data).rows,
    },
  ],
]);

// ---------------------------------------------------------------------------
// Public accessors
// ---------------------------------------------------------------------------

export { getConvertibleTargets };

export function getChartEntry(
  chartType: string,
): ChartRegistryEntry | undefined {
  return REGISTRY.get(chartType);
}

export function getChartRenderer(chartType: string) {
  return REGISTRY.get(chartType)?.renderer;
}


// Whether a chart/table can be converted to a Highcharts chart.
// data_table needs at least one numeric column to chart; line types can't be charted
// from table view because the multi-series data structure doesn't map to bar/pie rows.
export function canConvertToChart(sourceType: string, data: unknown): boolean {
  if (sourceType === "data_table") {
    return getDataTableChartRows(data).length > 0;
  }
  return (
    sourceType !== "line" &&
    sourceType !== "multi_line" &&
    sourceType !== "special_multi_line"
  );
}
